// Note: Non-component export will prevent fast refreshing in this file. (go/eslint/no-mixed-react-exports)
/* eslint-disable @stripe-internal/stripe/no-mixed-react-exports */

// To replace the hook or hoc when using this context in a shallow test, add this code above
// the top-most describe block
//
// jest.mock('@stripe-internal/ark-package/components/FlowConfigurationContext', () =>
//   require('@stripe-internal/ark-package/components/FlowConfigurationContext/testHelpers'),
// );

import React from 'react';
import type {
  dateValidations,
  DFValidationResult,
  SummarySection,
} from '@stripe-internal/ark-lib';
import type {ArkSectionName} from '../accounts/formCompositions/finalization/RequirementsIndexComposition/sections';

export interface CustomValidations {
  dateOfBirthMinAge?: dateValidations.DateValidator;
  /**
   * This validation is used when we do not want to allow minors to onboard, but do want to hint that a legal guardian can set up the account for them.
   *
   * This is currently only used in express, so the UX is this:
   *
   * Ages 0-13: Error you cannot onboarding. (This is enforced by the default minAge validator which errors on ages below 18)
   * Ages 13-17: Error you cannot onboard, but a legal guardian can set up the account for you. This is still blocked, but we provide a different error message.
   * Ages 18+: No error, you can onboard.
   */
  dateOfBirthMinorsAge?: dateValidations.DateValidator;
  verifyBusinessURL?: boolean;
  countryCell?: (arg1: string) => DFValidationResult | null | undefined;
}

export interface FlowConfigurationContextPayload {
  hardRequiredFields?: readonly string[];
  hardRequireAllFields?: boolean;
  optionalFields?: readonly string[];
  customValidations: CustomValidations;
  flow: {
    /**
     * Controls which requirements (currently_due, eventually_due,
     * previously_due) should be shown in the onboarding form.
     * */
    showFields: {
      currentlyDue?: boolean;
      eventuallyDue?: boolean;
      previouslyDue?: boolean;
    };
    intent: 'requestInformation' | 'allowInformationUpdate';
    satisfactionCriteria: 'none' | 'currentlyDue' | 'eventuallyDue';
    collectFutureRequirements: boolean;
  };
  showPayoutScheduleForm?: boolean;

  /**
   * Whether to explicitly exclude statement descriptor from requirements
   * this should be the case for custom and express accounts, where statement descriptor
   * should not be updateable, and sometimes pops up in previously_due
   */
  alwaysExcludeStatementDescriptor?: boolean;

  /**
   * The user viewing the page can be an Admin,
   * not necessarily the account opener
   */
  userCanBeAnyAdmin?: boolean;

  synchronousVerificationEnabled?: boolean;

  showToSInSummary?: boolean;

  /**
   * Whether the onboarding flow prompts the user to accept the Stripe Terms of Service.
   * Defaults to `false` if not provided.
   * When set to `true`, the user will exit the onboarding flow with the `tos_acceptance.date`
   * and `tos_acceptance.ip` requirements still unsatisfied.
   * This flag is also used for some minor copy changes near the end of the flow.
   * When setting this to `true` the platform is responsible for prompting the user to
   * accept the appropriate Stripe Terms of Service and recording their acceptance using
   * the [Accounts API](https://stripe.com/docs/api/accounts/update#update_account-tos_acceptance).
   */
  skipTermsOfServiceCollection?: boolean;

  /*
   * Whether clicking submit will activate Tax for standard connect merchant
   * and configure their origin address.
   * null is the case in case of an unsupported countries like sanctioned
   * countries.
   */
  stripeTaxConnectOnboardingShouldEnroll?: boolean | null;

  shardedOnboardingFlow?: {
    /**
     * Whether the sharded onboarding flow is active.
     */
    isActive?: boolean | null;
    /**
     * Sharded Onboarding Request ID
     */
    requestId: string | null;
    /**
     * Email of The Authorizer
     */
    recipientEmail?: string | null;

    useCase?: 'authorizer' | 'business_representative' | null;
  };

  disableIcons?: boolean;
  /*
   * Whether to show the verbose text version of the unified redirect page
   * after submitting the onboarding summary composition.
   */
  showVerboseRedirect?: boolean;
  /*
   * Whether to redirect to the Opal application when missing Treasury ToS.
   */
  shouldRedirectForTreasuryToS?: boolean;

  isOrrM5?: boolean | null;
  hideRequirementsNotice?: boolean;

  /*
   * Disables capability updates when updating the account (e.g. on country change)
   */
  lockInCapabilities?: boolean;

  requestedCapabilities?: ReadonlyArray<string>;

  /**
   * Whether to force the phone country code to the merchant's country regardless
   * of the previously entered phone value if changing the country code is
   * not allowed in the merchant's country. Fixes go/jira/AEX-742.
   * TODO(jimmyly, 2022-04-27): Clean up this flag if no related issues are reported.
   */
  forcePhoneCountryWhenChangeNotAllowed?: boolean;

  /*
   * Whether to disable industry selector for platforms (such as the Connected
   * Account Detail page) because of risk manual override
   */
  platformControlsDisabled?: boolean;

  /*
   * Whether to show the SSA transference warning and re-submit tos_agreement
   * when editing a verified TIN
   */
  showPlatformSSATransference?: boolean;

  /*
   * Whether to allow the composition router to render a composition if the
   * current URL path has a subpath after the composition path. For example,
   * if this flag is true then a composition with the path `/summary` would
   * be rendered if the current URL path is `/summary/company/update`.
   */
  allowCompositionSubPath?: boolean;

  /*
   * If the person at the keyboard is a platform user rather than the connected
   * account holder, we might hide some UI that only makes sense for account
   * holders.
   */
  isPlatformUser?: boolean;

  stripeThirdPartyBaseUrl?: string;

  /* Allow users to invite a teammate to their account from the account app */
  allowInviteATeammate?: boolean;

  /* Is the user allowed to submit account app or not */
  accountAppSubmissionDisabled?: boolean;

  /**
   * When enabled we will enable routing from the RequirementsIndexComposition.
   * This way when a connected account clicks on the edit link of any section
   * we'll update the URL to load the corresponding section.
   */
  enableRequirementsIndexRouting?: boolean;

  createLegalGuardianEnabled?: boolean;

  /**
   * If ARK is rendered in a Stripe-hosted iframe, i.e. embedded account onboarding/management, we need to render the UploadPreview component in an iframe.
   * This is because the Stripe-hosted iframe has stricter CSP rules and requires a separate iframe
   * that only has the CSP directive frame-src ':blob'.
   */
  uploadPreviewFrameSrc?: URL;

  /**
   * Whether to show the risk intervention composition.
   */
  showRiskInterventionComposition?: boolean;

  /**
   * Whether to show the bank account verification document alternative methods UI. This is
   * currently determined by the `enable_bank_account_verification_alternative_methods` flag and being used
   * in HostedOnboardingArkv2
   */
  showBankAccountVerificationAlternativeMethods?: boolean;

  /**
   * Whether to show the bank account verification document upload composition. This is
   * currently determined by the `enable_bank_account_verification_upload_flow` flag and being used
   * in HostedOnboardingArkv2
   */
  showBankAccountDocUploadComposition?: boolean;

  /**
   * Whether to show the ability to change name fields in-line in the UI for resolving Bank Account Verification.
   * This is currently determined by the `enable_bank_account_verification_name_update` flag and being used
   * in HostedOnboardingArkv2
   */
  showBankAccountVerificationNameUpdate?: boolean;

  /**
   * Show the new legal entity selection flow in the unified business type composition.
   * Note: even if this flag is enabled the new legal entity selection flow
   * may still be hidden if the user does not meet further requirements (e.g. in an enabled country)
   * See definition for the `useNewLegalEntitySelectionFlowEnabled()` hook.
   */
  showNewLegalEntitySelectionFlow?: boolean;

  /**
   * Show the tax details composition in the flow for collecting tax-related
   * information. Note: even if this flag is enabled the tax details composition
   * may still be hidden if it is not applicable to the user. See definition for
   * the `isTaxDetailsCompositionApplicable()` function.
   */
  showTaxDetailsComposition?: boolean;

  /**
   * True if the flow is eligible for showing the climate composition to upsell Stripe Climate.
   * Note that there are other requirements for showing climate, so the composition may be hidden
   * even if this flag is true.
   */
  showClimateComposition?: boolean;

  /**
   * If the user is on the direct settings accessing the AccountManagement embedded component,
   * instead of allowing them to upload a bank account document we redirect them to ASH. This
   * is used in the AccountManagement embedded component
   */
  redirectBankOwnershipUploadToAccountStatusDashboardPage?: boolean;

  /**
   * If the user is on direct settings their Singpass URL Endpoint should be overridden to
   * the dashboardEndpoint rather than using the connectEndpoint
   */
  singpassURLEndpoint?: string;

  /**
   * If the host endpoint the user is currently accessing is the stripe dashboard
   */
  hostedByDirect?: boolean;

  /**
   * Passed in from Standard Oauth when we connecting an existing account to another platform.
   */
  preselectedLegalEntityToken?: string;

  /**
   * Whether to use the platform's branding settings (i.e. branding settings set in the platform's Stripe dashboard) for the onboarding flow.
   * In general, this will be `true` when ARK is inside one of our own surfaces (like hosted onboarding), and `false` when
   * inside a platform's surface (like embedded onboarding).
   */
  usePlatformBrandingSettings?: boolean;

  /**
   * Overrides the behaviour of Product Description and Business URL fields being
   * toggleable, and instead shows them as separate fields
   */
  disableProductDescriptionAndUrlEitherOrBehaviour?: boolean;

  /**
   * Array of sections to disable in the requirements index.
   */
  disabledRequirementsIndexSections?: Extract<
    ArkSectionName,
    'company' | 'person' | 'support' | 'externalAccount'
  >[];

  /**
   * Array of sections to hide in the requirements index.
   */
  hiddenRequirementsIndexSections?: Extract<
    ArkSectionName,
    'climate' | 'taxDirect' | 'taxProductConnect' | 'externalAccount' | 'risk'
  >[];

  /**
   * SHOULD BE REMOVED!
   *
   * For Direct Onboarding, we need to customize the RequirementsIndex
   * to include a 2FA section. Rather than forking the OnboardingRequirementsIndex
   * for this single purpose this allows consumers to inject the 2FA section to
   * be used.
   *
   * The default implementation returns null hiding the section.
   */
  useTwoFactorAuthSection?: () => SummarySection | null;

  /**
   * Enable the expanded person behavior. This behavior allows for collecting
   * additional person requirements. Note: this is turned on automatically for
   * some countries.
   */
  isExpandedPersonEnabled?: boolean;

  /**
   * Configuration options for the BOOwnersComposition.
   */
  boOwnersComposition?: {
    /**
     * By default, people displayed in BOOwnersComposition are not editable.
     * This config option allows the user to edit the person details. Note:
     * this is turned on automatically for some countries.
     */
    allowPersonEditing?: boolean;

    /**
     * By default, the BOOwnersComposition only collects these requirements:
     * `company.owners_provided`, `relationship.owner` and `owners.*`. This
     * config option allows BOOwnersComposition to also collect `person_xxx.*`
     * requirements for any person with the `owner` relationship. Note: this
     * will automatically enable person editing in order to collect person
     * requirements, even if the `allowPersonEditing` config option is false.
     */
    collectPersonRequirements?: boolean;

    /**
     * Prevent the user from proceeding past the BOOwnersComposition if the
     * composition is not completed.
     */
    disableProceedWhenCompositionIncomplete?: boolean;
  };

  /**
   * When disabled hides document upload fields on the PersonUpdate form.
   */
  showPersonDocumentFieldsOnPersonUpdate?: boolean;
}

export const FlowConfigurationContext =
  React.createContext<FlowConfigurationContextPayload | null>(null);

FlowConfigurationContext.displayName = 'FlowConfigurationContext';

export const useFlowConfigurationContext: () => FlowConfigurationContextPayload =
  () => {
    const value = React.useContext(FlowConfigurationContext);
    if (!value) {
      throw new Error('FlowConfigurationContext was consumed before defined');
    }
    return value;
  };

export const useOptionalFlowConfigurationContext = () =>
  React.useContext(FlowConfigurationContext);

export interface FlowConfigurationContextProps {
  flowConfigurationContext: FlowConfigurationContextPayload;
}

const hoc = <
  T extends FlowConfigurationContextProps = FlowConfigurationContextProps,
>(
  WrappedComponent: React.ComponentType<T>,
) => {
  const ComponentWithFlowConfigurationContext = (
    props: Omit<T, keyof FlowConfigurationContextProps>,
  ) => {
    return (
      <WrappedComponent
        {...(props as T)}
        flowConfigurationContext={useFlowConfigurationContext()}
      />
    );
  };

  // Try to create a nice displayName for React Dev Tools
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  ComponentWithFlowConfigurationContext.displayName = `withFlowConfigurationContext(${displayName})`;

  return ComponentWithFlowConfigurationContext;
};

export default {hoc};
